import { useEffect } from 'react';
import { Card, Typography, Empty, Spin, Tag, Timeline, Alert, Flex, Space, Button } from 'antd';
import { CheckCircleFilled, CheckCircleOutlined, PauseCircleOutlined, CoffeeOutlined, CalendarOutlined, VideoCameraOutlined, PlayCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { EntityImage } from '../Entities/EntityImage';
import NodeLogo from '../Node/NodeLogo';
import { useMemberGathering, useServerClock } from '../../hooks/use_gathering';
import { useNodeInfo } from '../../hooks/use_node_info';
import { flowPosition, flowTimeline, parseServerTime, gatheringCallUrl } from '../../utils/gathering';
import { isGuestPerson } from '../../utils/guest_session';
import FlowTimeline, { stepLabels, stepTimes, StepKindIcon, StepCountdown } from './FlowTimeline';

const { Title, Text, Paragraph } = Typography;

/** The node's logo and name, centred — as on the guest join page. */
function NodeHero({ children }) {
    const { nodeName } = useNodeInfo();
    return (
        <Flex vertical gap={4} align="center" className="gathering-hero">
            <NodeLogo size={56} />
            <Title level={3} style={{ margin: '8px 0 0' }}>{nodeName || 'Gathering'}</Title>
            {children}
        </Flex>
    );
}

/** A lone card in the middle of the page, for the states before it opens. */
function CentreCard({ children }) {
    return (
        <div className="gathering-member-centre">
            <Card className="gathering-join-card">
                <Flex vertical gap={12} align="center" style={{ textAlign: 'center' }}>{children}</Flex>
            </Card>
        </div>
    );
}

/**
 * What everyone sees: first the groups on offer (tap to mark yourself in — as
 * many as fit you; it's what rooms are planned from), then the flow — your
 * schedule, with your room, topic and roommates in each breakout round, live
 * once the host starts it.
 * `colorOf(group)` maps a group to its palette colour; `onStatus(status)`
 * hears where the gathering is ('closed' | 'waiting' | 'open') on each load.
 */
// `instanceId`: an app gathering (sdk_apps/gathering.py) rather than the
// node's; its live flow page is under the app's own path.
function MemberView({ colorOf, onStatus, instanceId = null }) {
    const navigate = useNavigate();
    // Loaded here rather than in the page, so switching in from the admin
    // view always shows the gathering as it is now.
    const { data, loading, error, setMyGroups } = useMemberGathering({ instanceId });
    const startedAt = parseServerTime(data?.flow_started_at);
    const pausedAt = parseServerTime(data?.flow_paused_at);
    const paused = startedAt != null && pausedAt != null;
    const now = useServerClock(data?.server_now, startedAt != null && !paused);
    const status = data?.status;
    // Only once loaded: switching in from the admin view keeps the last
    // known status (and its backdrop) rather than flickering through null.
    useEffect(() => { if (status && onStatus) onStatus(status); }, [status, onStatus]);

    if (loading) return <div className="gathering-member-centre"><Spin size="large" /></div>;
    if (error) return <CentreCard><NodeHero /><Text type="danger">Couldn't load the gathering: {error}</Text></CentreCard>;
    if (!data) return null;

    // Not open yet: "no gathering" (with the host's note) or the waiting
    // room. The page keeps polling (see useMemberGathering), so it changes
    // by itself when the host moves the gathering on.
    if (data.status === 'closed') {
        return (
            <CentreCard>
                <NodeHero>
                    <Text strong><CalendarOutlined style={{ color: 'var(--text-tertiary)' }} /> No gathering right now</Text>
                </NodeHero>
                {data.message
                    ? <Paragraph className="gathering-closed-message">{data.message}</Paragraph>
                    : <Paragraph type="secondary" style={{ margin: 0 }}>Check back later — this page updates by itself when one opens.</Paragraph>}
            </CentreCard>
        );
    }
    if (data.status === 'waiting') {
        return (
            <CentreCard>
                <NodeHero>
                    <Text strong><CoffeeOutlined style={{ color: 'var(--vellby-sky-500)' }} /> You're in the waiting room</Text>
                </NodeHero>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                    The gathering is about to begin. This page opens by itself when the host lets everyone in — no need to refresh.
                </Paragraph>
                <Spin size="small" />
            </CentreCard>
        );
    }

    // Opened with nothing set up: one room, everyone in it (the backend's
    // is_single_room) — no groups to pick, no schedule to follow.
    if (data.single_room) {
        const room = data.common_hangout_room_name;
        return (
            <CentreCard>
                <NodeHero>
                    <Text strong><TeamOutlined style={{ color: 'var(--status-success)' }} /> The gathering is open</Text>
                </NodeHero>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                    {room
                        ? 'Everyone’s together in one room — come on in.'
                        : 'Everyone’s meeting in one room, but video calls aren’t available here right now.'}
                </Paragraph>
                {room && (
                    <Button
                        type="primary"
                        size="large"
                        icon={<VideoCameraOutlined />}
                        onClick={() => navigate(gatheringCallUrl(room, 'Gathering'))}
                    >
                        Join the room
                    </Button>
                )}
            </CentreCard>
        );
    }

    const mine = new Set(data.my_group_ids);
    const toggle = id => setMyGroups(mine.has(id) ? data.my_group_ids.filter(g => g !== id) : [...data.my_group_ids, id]);
    const schedule = data.schedule ?? [];
    const timeline = flowTimeline(schedule);
    const labels = stepLabels(schedule);
    // A paused flow is frozen at the moment the host paused it.
    const position = flowPosition(schedule, startedAt, paused ? pausedAt : now);
    const published = !!data.published_at;
    const liveIndex = position && !position.finished ? position.index : -1;

    const stepContent = (step, i) => {
        const room = step.room;
        return (
            <Flex vertical gap={6} className={i < liveIndex || position?.finished ? 'gathering-schedule-done' : undefined}>
                <Space wrap size={8}>
                    <Text strong>{labels[i]}</Text>
                    <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                        {stepTimes(startedAt, step)} · {step.duration_minutes} min
                    </Text>
                    {step.extended_minutes > 0 && <Tag color="success" style={{ margin: 0 }}>+{step.extended_minutes} min added</Tag>}
                    {i === liveIndex && <Tag color={paused ? 'warning' : 'processing'} style={{ margin: 0 }}>{paused ? 'Paused' : 'Now'}</Tag>}
                </Space>
                {i === liveIndex && (
                    <StepCountdown label={labels[i]} remainingMs={position.remainingMs} paused={paused} />
                )}
                {step.kind === 'common' ? (
                    <Flex vertical gap={8} align="flex-start">
                        <Text type="secondary">Everyone together in the common room</Text>
                        {i === liveIndex && data.common_hangout_room_name && (
                            <Button
                                type="primary"
                                size="small"
                                icon={<VideoCameraOutlined />}
                                onClick={() => navigate(gatheringCallUrl(data.common_hangout_room_name, 'Common room'))}
                            >
                                Join common room
                            </Button>
                        )}
                    </Flex>
                ) : room ? (
                    <Card size="small" className="gathering-schedule-room">
                        <Flex vertical gap={8}>
                            <Flex wrap gap={8} align="center" justify="space-between">
                                <Text strong>
                                    Room {room.index + 1} <Text type="secondary" style={{ fontWeight: 'normal' }}>of {room.room_count}</Text>
                                </Text>
                                {i === liveIndex && room.hangout_room_name && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<VideoCameraOutlined />}
                                        onClick={() => navigate(gatheringCallUrl(room.hangout_room_name, `Room ${room.index + 1}`))}
                                    >
                                        Join room
                                    </Button>
                                )}
                            </Flex>
                            {room.topic && (
                                <Alert type="info" title="Your topic" description={room.topic} />
                            )}
                            <Flex wrap gap={6}>
                                {room.people.map(p => (
                                    <Tag key={p.id} className="gathering-schedule-person" color={p.id === data.my_person_id ? 'blue' : undefined}>
                                        <EntityImage entity={p} size={18} />
                                        {p.name}{p.id === data.my_person_id ? ' (you)' : isGuestPerson(p) ? ' (guest)' : ''}
                                    </Tag>
                                ))}
                            </Flex>
                        </Flex>
                    </Card>
                ) : (
                    <Text type="secondary">
                        {published ? "You're not in a room for this round yet — the host can add you." : 'Your room will appear here.'}
                    </Text>
                )}
            </Flex>
        );
    };

    return (
        <div className="gathering-member">
            <NodeHero>
                <Paragraph type="secondary" className="gathering-hero-lede">
                    Tell us which groups you belong to, and we’ll put you in a room with a good mix of people.
                </Paragraph>
            </NodeHero>
            <Card className="gathering-section" title={<span><span className="gathering-step">1</span>Your groups</span>}>
                {data.groups.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="The host hasn't set up any groups yet" />
                ) : !data.my_person_id ? (
                    <Text type="secondary">You need a profile on this node before you can join a group.</Text>
                ) : (
                    <>
                        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                            Pick every group that fits you — you can be in more than one.
                        </Paragraph>
                        <div className="gathering-choices">
                            {data.groups.map(g => {
                                const on = mine.has(g.id);
                                return (
                                    <button
                                        key={g.id}
                                        type="button"
                                        className={`gathering-choice${on ? ' is-on' : ''}`}
                                        style={{ '--group-color': colorOf(g) }}
                                        onClick={() => toggle(g.id)}
                                        aria-pressed={on}
                                    >
                                        <span className="gathering-choice-dot" />
                                        <span className="gathering-choice-name">{g.name}</span>
                                        {on && <CheckCircleFilled className="gathering-choice-check" />}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </Card>

            <Card
                className="gathering-section"
                title={<span><span className="gathering-step">2</span>Your schedule</span>}
                extra={!position?.finished && (
                    <Space size={8}>
                        {liveIndex >= 0 && (paused
                            ? <Tag color="warning" style={{ margin: 0 }}>Paused</Tag>
                            : <Tag color="processing" style={{ margin: 0 }}>Live</Tag>)}
                        {/* The live flow page: the right room for every step, with a
                            transition between them (GatheringFlow.jsx). Before the
                            start it's a waiting room that moves on by itself. */}
                        <Button
                            type={liveIndex >= 0 ? 'primary' : 'default'}
                            size="small"
                            icon={<PlayCircleOutlined />}
                            onClick={() => navigate(instanceId ? `/app/${instanceId}/flow` : '/gathering/flow')}
                        >
                            Enter the gathering
                        </Button>
                    </Space>
                )}
            >
                <Flex vertical gap={20}>
                    {!published && (
                        <Alert type="info" showIcon title="Rooms haven't been set yet" description="They'll appear here once the host shares them." />
                    )}
                    <FlowTimeline steps={schedule} position={position} startedAt={startedAt} paused={paused} />
                    <Timeline
                        items={timeline.map((step, i) => ({
                            key: step.step_id,
                            color: i === liveIndex ? (paused ? 'orange' : 'blue') : (i < liveIndex || position?.finished) ? 'gray' : 'blue',
                            icon: i === liveIndex && paused ? <PauseCircleOutlined />
                                : (i < liveIndex || position?.finished) ? <CheckCircleOutlined />
                                    : <StepKindIcon kind={step.kind} />,
                            content: stepContent(step, i),
                        }))}
                    />
                </Flex>
            </Card>
        </div>
    );
}

export default MemberView;
